import * as z from 'zod';
import { Button, ButtonGroup, Checkbox, createListCollection, Field, Input, Portal, Select, Separator, Spacer, Stack } from '@chakra-ui/react';
import { daysOrder, formatWeekday, locations } from '@opanuj/shared/utils';
import { get, ref, set, update } from 'firebase/database';
import { Lesson, Slot } from '@opanuj/shared/types';
import { useDatabase } from '../../../hooks/useDatabase';
import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { useOfferListKeys } from '../../../hooks/useOffer';
import { useTeachersListKeys } from '../../../hooks/useTeachers';
import { zodResolver } from '@hookform/resolvers/zod';

type SlotWithRuntimeData = Partial<Slot> & {
	lessons?: Array<Record<string, any>>;
	reservations?: Array<Record<string, any>>;
};

export const sanitizeSlotPayload = (values: Record<string, any>, existingSlot?: SlotWithRuntimeData | null) => {
	const { applyEditFromDate, codes, locations, ...rest } = values ?? {};
	const sanitized = { ...rest };

	if (Array.isArray(existingSlot?.lessons)) {
		sanitized.lessons = existingSlot.lessons;
	}

	if (Array.isArray(existingSlot?.reservations)) {
		sanitized.reservations = existingSlot.reservations;
	}

	return sanitized;
};

const createSlotSchema = (isEditing: boolean) =>
	z.object({
		code: z.string().optional(),
		codes: z.array(z.string()).default([]),
		defaultTeacher: z.string().min(1, { message: 'Pole wymagane' }),
		day: z.string().min(1, { message: 'Pole wymagane' }),
		time: z.string().regex(/([0-1][0-9]|[2][0-3]):([0-5][0-9])/, ({ message: 'Format gg:mm' })),
		duration: z.coerce.number().min(1, { message: 'Pole wymagane' }),
		location: z.string().min(1, { message: 'Pole wymagane' }),
		locations: z.array(z.string()).default([]),
		room: z.string().optional(),
		hidden: z.boolean(),
		started: z.boolean(),
		id: z.string().default(''),
		applyEditFromDate: z.string().optional(),
	}).superRefine((data, ctx) => {
		if (!isEditing && data.codes.length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['codes'],
				message: 'Wybierz co najmniej jeden kod',
			});
		}

		if (isEditing && !data.code) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['code'],
				message: 'Pole wymagane',
			});
		}

		if (data.location && data.location !== 'online' && (!data.room || !data.room.trim())) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['room'],
				message: 'Pole wymagane, jeśli nie online',
			});
		}
	});

export const SlotForm = ({ firstFieldRef, onCancel, slot, initialValues }: any) => {
	const isEditing = !!slot;
	if (isEditing) console.info('Editing', slot);

	const defaultValues = {
		started: false,
		hidden: false,
		codes: [],
		code: '',
		locations: [],
		...(initialValues ?? {}),
		...(slot ?? {}),
		applyEditFromDate: new Date().toISOString().split('T')[0],
	};
	const database = useDatabase();
	const {
		handleSubmit,
		register,
		formState: { errors, isSubmitting },
		reset,
		watch,
		setValue,
		setError,
	} = useForm({
		resolver: zodResolver(createSlotSchema(isEditing)),
		defaultValues,
		mode: 'onBlur',
	});
	const values = watch();
	const selectedCodes = watch('codes') ?? [];
	const selectedLocations = watch('locations') ?? [];

	const onSubmit = (submitValues: Slot & { applyEditFromDate: string; codes?: string[]; locations?: string[] }): Promise<void> => {
		return new Promise(async (resolve) => {
			if (!isEditing) {
				const codesToCreate = (submitValues.codes ?? []).length > 0 ? submitValues.codes ?? [] : [submitValues.code].filter(Boolean);
				if (codesToCreate.length === 0) {
					setError('codes', { type: 'manual', message: 'Wybierz co najmniej jeden kod' });
					setValue('codes', [], { shouldValidate: true, shouldDirty: true });
					resolve();
					return;
				}

				const normalizedLocations = (Array.isArray(submitValues.locations) && submitValues.locations.length > 0)
					? submitValues.locations.filter((value) => typeof value === 'string' && Boolean(value.trim()))
					: [submitValues.location].filter((value) => typeof value === 'string' && Boolean(value.trim()));
				const locationsToCreate = normalizedLocations.length > 0 ? normalizedLocations : [];
				if (locationsToCreate.length === 0) {
					setError('location', { type: 'manual', message: 'Wybierz co najmniej jedną lokalizację' });
					setValue('location', '', { shouldValidate: true, shouldDirty: true });
					setValue('locations', [], { shouldValidate: true, shouldDirty: true });
					resolve();
					return;
				}

				setValue('locations', locationsToCreate, { shouldValidate: true, shouldDirty: true });
				setValue('location', locationsToCreate[0], { shouldValidate: true, shouldDirty: true });

				for (const code of codesToCreate) {
					for (const location of locationsToCreate) {
						const newSlot = sanitizeSlotPayload({
							...submitValues,
							code,
							location,
							id: crypto.randomUUID(),
							started: false,
						}, null);
						await set(ref(database, `slots/${newSlot.id}`), newSlot);
					}
				}
				resolve();
				onCancel();
				return;
			}

			submitValues.id = submitValues.id || crypto.randomUUID();
			const slotPayload = sanitizeSlotPayload(submitValues, slot);
			await set(ref(database, `slots/${submitValues.id}`), slotPayload);

			if (isEditing && slot.started) {
				await get(ref(database, `lessons`)).then((snapshot) => {
					const lessonsData = snapshot.val();
					const lessonsToUpdate = Object.entries<Lesson>(lessonsData ?? {}).filter(([lessonId, lesson]) => lesson.sourceSlot === slot.id && (!submitValues.applyEditFromDate || lesson.timestamp >= Math.floor(new Date(submitValues.applyEditFromDate + 'T00:00:00').getTime() / 1000)));
					return lessonsToUpdate;
				}).then(async (lessonsToUpdate) => {
					const updates: { [path: string]: Lesson } = {};
					lessonsToUpdate.forEach(([lessonId, lesson]) => {
						const updatedLesson = {
							...lesson,
							code: submitValues.code,
							duration: submitValues.duration,
							location: submitValues.location,
							room: submitValues.room,
							teacher: submitValues.defaultTeacher,
							timestamp: (() => {
								const dayDiff = (daysOrder.indexOf(submitValues.day) + 7 - daysOrder.indexOf(slot.day)) % 7;
								const [hours, minutes] = submitValues.time.split(':').map(Number);
								const originalDate = new Date(lesson.timestamp * 1000);
								originalDate.setDate(originalDate.getDate() + dayDiff);
								originalDate.setHours(hours, minutes, 0, 0);
								return Math.floor(originalDate.getTime() / 1000);
							})(),
						};
						updates[`lessons/${lessonId}`] = updatedLesson;
					});
					if (Object.keys(updates).length > 0) {
						await update(ref(database), updates);
					}
				});
			}

			resolve();
			onCancel();
		});
	};

	const [codesList, isOfferLoading] = useOfferListKeys();
	const [teachersList, areTeachersLoading] = useTeachersListKeys();
	const locationCollection = useMemo(
		() => createListCollection({
			items: (locations ?? []).map((value) => ({ label: value, value })),
		}),
		[]
	);
	const getErrorMessage = (message?: string | { message?: string } | undefined) =>
		typeof message === 'string' ? message : message?.message ?? '';
	const codeCollection = useMemo(
		() => createListCollection({
			items: (codesList ?? []).map((value) => ({ label: value, value })),
		}),
		[codesList]
	);

	return (
		<Stack asChild gap={2}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Field.Root invalid={Boolean(!isEditing ? errors.codes?.message : errors.code?.message)}>
					<Field.Label>Kod</Field.Label>
					{!isEditing ? (
						<Select.Root
							collection={codeCollection}
							multiple
							value={selectedCodes}
							disabled={isOfferLoading}
							onValueChange={(details) => {
								setValue('codes', details.value, { shouldValidate: true, shouldDirty: true });
							}}
						>
							<Select.HiddenSelect />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder="Wybierz kod" />
								</Select.Trigger>
								<Select.IndicatorGroup>
									<Select.ClearTrigger />
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content zIndex={2000}>
										{codeCollection.items.map((item) => (
											<Select.Item key={item.value} item={item}>
												<Select.ItemIndicator />
												<Select.ItemText>{item.label}</Select.ItemText>
											</Select.Item>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					) : (
						<Select.Root
							collection={codeCollection}
							value={watch('code') ? [watch('code')] : []}
							disabled={isOfferLoading}
							onValueChange={(details) => {
								setValue('code', details.value[0] ?? '', { shouldValidate: true, shouldDirty: true });
							}}
						>
							<Select.HiddenSelect {...register('code')} />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder="Wybierz kod" />
								</Select.Trigger>
								<Select.IndicatorGroup>
									<Select.ClearTrigger />
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content zIndex={2000}>
										{codeCollection.items.map((item) => (
											<Select.Item key={item.value} item={item}>
												<Select.ItemIndicator />
												<Select.ItemText>{item.label}</Select.ItemText>
											</Select.Item>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					)}
					<Field.ErrorText>{!isEditing ? getErrorMessage(errors.codes?.message) : getErrorMessage(errors.code?.message)}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.defaultTeacher?.message)}>
					<Field.Label>Korepetytor</Field.Label>
					<Select.Root
						collection={createListCollection({ items: (teachersList ?? []).map((value) => ({ label: value, value })) })}
						disabled={areTeachersLoading}
						value={watch('defaultTeacher') ? [watch('defaultTeacher')] : []}
						onValueChange={(details) => {
							setValue('defaultTeacher', details.value[0] ?? '', { shouldValidate: true, shouldDirty: true });
						}}
					>
						<Select.HiddenSelect {...register('defaultTeacher')} />
						<Select.Control>
							<Select.Trigger>
								<Select.ValueText placeholder="Wybierz korepetytora" />
							</Select.Trigger>
							<Select.IndicatorGroup>
								<Select.ClearTrigger />
								<Select.Indicator />
							</Select.IndicatorGroup>
						</Select.Control>
						<Portal>
							<Select.Positioner>
								<Select.Content zIndex={2000}>
									{(teachersList ?? []).map((value) => (
										<Select.Item key={value} item={{ label: value, value }}>
											<Select.ItemIndicator />
											<Select.ItemText>{value}</Select.ItemText>
										</Select.Item>
									))}
								</Select.Content>
							</Select.Positioner>
						</Portal>
					</Select.Root>
					<Field.ErrorText>{getErrorMessage(errors.defaultTeacher?.message)}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.day?.message)}>
					<Field.Label>Dzień</Field.Label>
					<Select.Root
						collection={createListCollection({ items: (daysOrder ?? []).map((value) => ({ label: formatWeekday(value), value })) })}
						value={watch('day') ? [watch('day')] : []}
						onValueChange={(details) => {
							setValue('day', details.value[0] ?? '', { shouldValidate: true, shouldDirty: true });
						}}
					>
						<Select.HiddenSelect {...register('day')} />
						<Select.Control>
							<Select.Trigger>
								<Select.ValueText placeholder="Wybierz dzień" />
							</Select.Trigger>
							<Select.IndicatorGroup>
								<Select.ClearTrigger />
								<Select.Indicator />
							</Select.IndicatorGroup>
						</Select.Control>
						<Portal>
							<Select.Positioner>
								<Select.Content zIndex={2000}>
									{(daysOrder ?? []).map((value) => (
										<Select.Item key={value} item={{ label: formatWeekday(value), value }}>
											<Select.ItemIndicator />
											<Select.ItemText>{formatWeekday(value)}</Select.ItemText>
										</Select.Item>
									))}
								</Select.Content>
							</Select.Positioner>
						</Portal>
					</Select.Root>
					<Field.ErrorText>{getErrorMessage(errors.day?.message)}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.time?.message)}>
					<Field.Label>Godzina (gg:mm):</Field.Label>
					<Input type="time" step={60} {...register('time')} />
					<Field.ErrorText>{getErrorMessage(errors.time?.message)}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.duration?.message)}>
					<Field.Label>Długość (min)</Field.Label>
					<Input type="number" min={1} step={1} {...register('duration')} />
					<Field.ErrorText>{String(errors.duration?.message ?? '')}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.location?.message)}>
					<Field.Label>Lokacja</Field.Label>
					{!isEditing ? (
						<Select.Root
							collection={locationCollection}
							multiple
							value={selectedLocations}
							onValueChange={(details) => {
								setValue('locations', details.value, { shouldValidate: true, shouldDirty: true });
								setValue('location', details.value[0] ?? '', { shouldValidate: true, shouldDirty: true });
							}}
						>
							<Select.HiddenSelect />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder="Wybierz miejsce / placówkę" />
								</Select.Trigger>
								<Select.IndicatorGroup>
									<Select.ClearTrigger />
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content zIndex={2000}>
										{locationCollection.items.map((item) => (
											<Select.Item key={item.value} item={item}>
												<Select.ItemIndicator />
												<Select.ItemText>{item.label}</Select.ItemText>
											</Select.Item>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					) : (
						<Select.Root
							collection={locationCollection}
							value={watch('location') ? [watch('location')] : []}
							onValueChange={(details) => {
								setValue('location', details.value[0] ?? '', { shouldValidate: true, shouldDirty: true });
							}}
						>
							<Select.HiddenSelect {...register('location')} />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder="Wybierz miejsce / placówkę" />
								</Select.Trigger>
								<Select.IndicatorGroup>
									<Select.ClearTrigger />
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content zIndex={2000}>
										{locationCollection.items.map((item) => (
											<Select.Item key={item.value} item={item}>
												<Select.ItemIndicator />
												<Select.ItemText>{item.label}</Select.ItemText>
											</Select.Item>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					)}
					<Field.ErrorText>{getErrorMessage(errors.location?.message)}</Field.ErrorText>
				</Field.Root>

				<Field.Root invalid={Boolean(errors.room?.message)}>
					<Field.Label>Pokój</Field.Label>
					<Input
						{...register('room')}
						disabled={values.location === 'online'}
						readOnly={values.location === 'online'}
					/>
					<Field.ErrorText>{getErrorMessage(errors.room?.message)}</Field.ErrorText>
				</Field.Root>

				{isEditing && slot.started && (
					<Field.Root invalid={Boolean(errors.applyEditFromDate?.message)}>
						<Field.Label>Zastosuj zmiany od daty</Field.Label>
						<Input type="date" {...register('applyEditFromDate')} />
						<Field.ErrorText>{getErrorMessage(errors.applyEditFromDate?.message)}</Field.ErrorText>
					</Field.Root>
				)}

                <Separator my={1} />

				<Checkbox.Root
					checked={Boolean(values.hidden ?? false)}
					onCheckedChange={(event) => {
						setValue('hidden', event.checked === true, { shouldValidate: true, shouldDirty: true });
					}}
				>
					<Checkbox.HiddenInput />
					<Checkbox.Control>
						<Checkbox.Indicator />
					</Checkbox.Control>
					<Checkbox.Label>{isEditing ? 'Ukryty' : 'Dodaj termin(y) jako ukryte'}</Checkbox.Label>
				</Checkbox.Root>

				<ButtonGroup display="flex" justifyContent="flex-end" mt={4}>
					<Button variant="outline" onClick={reset} disabled={isSubmitting}>
						Resetuj
					</Button>
					<Spacer />
					<Button variant='ghost' onClick={onCancel} disabled={isSubmitting}>
						Anuluj
					</Button>
					<Button colorPalette="orange" loading={isSubmitting} type="submit">
						{isEditing ? 'Zapisz' : 'Dodaj'}
					</Button>
				</ButtonGroup>
			</form>
		</Stack>
	);
};
